import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, GitBranch, Plus, Save, Send, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiClient } from '../lib/api'
import { ProposalAssetsTab } from './ProposalAssetsTab'
import { ProposalHistoryDialog } from './ProposalHistoryDialog'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { useConfirm } from './ui/ConfirmDialog'
import { Drawer } from './ui/Drawer'
import { Input, Label, Select, Textarea } from './ui/Input'

type Proposal = {
  id: string
  code: string
  revision: number
  type: 'PRV' | 'CTR' | 'DPA'
  status: string
  title: string
  subtitle: string | null
  validityDays: number
  validUntil: string | null
  content: Record<string, unknown>
}

type Tab =
  | 'meta'
  | 'parti'
  | 'oggetto'
  | 'durata'
  | 'dati'
  | 'subresp'
  | 'toms'
  | 'notifica'
  | 'comunicazioni'
  | 'firme'
  | 'asset'

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'meta', label: 'Metadati' },
  { id: 'parti', label: 'Parti' },
  { id: 'oggetto', label: 'Oggetto · Finalità' },
  { id: 'durata', label: 'Durata · Cessazione' },
  { id: 'dati', label: 'Natura dati' },
  { id: 'subresp', label: 'Sub-responsabili' },
  { id: 'toms', label: 'TOMs' },
  { id: 'notifica', label: 'Notifica · Audit' },
  { id: 'comunicazioni', label: 'Comunicazioni · Foro' },
  { id: 'firme', label: 'Firme' },
  { id: 'asset', label: 'Asset' },
]

// biome-ignore lint/suspicious/noExplicitAny: editing freeform structured content
type DPAContent = any

const SPECIAL_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'origine_etnica', label: 'origine razziale o etnica' },
  { key: 'opinioni_politiche', label: 'opinioni politiche' },
  { key: 'convinzioni_religiose', label: 'convinzioni religiose o filosofiche' },
  { key: 'iscrizione_sindacale', label: 'appartenenza sindacale' },
  { key: 'dati_genetici', label: 'dati genetici' },
  { key: 'dati_biometrici', label: 'dati biometrici per identificazione' },
  { key: 'dati_sanitari', label: 'dati relativi alla salute' },
  { key: 'orientamento_sessuale', label: 'vita o orientamento sessuale' },
]

export function DPAEditorDrawer({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [tab, setTab] = useState<Tab>('meta')
  const [content, setContent] = useState<DPAContent | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [meta, setMeta] = useState({
    title: '',
    subtitle: '',
    validityDays: '60',
  })

  const proposalQ = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () =>
      apiClient.get<{ found: boolean; proposal: Proposal }>(`/proposals/${proposalId}`),
  })

  useEffect(() => {
    if (proposalQ.data?.found) {
      const p = proposalQ.data.proposal
      setContent(p.content)
      setMeta({
        title: p.title,
        subtitle: p.subtitle ?? '',
        validityDays: String(p.validityDays),
      })
    }
  }, [proposalQ.data])

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.patch(`/proposals/${proposalId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  const send = useMutation({
    mutationFn: () => apiClient.post(`/proposals/${proposalId}/status`, { status: 'sent' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  function update(path: string[], value: unknown) {
    setContent((prev: DPAContent) => setDeep(prev, path, value))
  }

  function onSave() {
    save.mutate({
      title: meta.title,
      subtitle: meta.subtitle || undefined,
      validityDays: Number(meta.validityDays) || 60,
      content,
    })
  }

  if (proposalQ.isLoading || !content) {
    return (
      <Drawer open onClose={onClose} title="Caricamento…" width="lg">
        <div className="p-6 text-zinc-500 text-sm">Caricamento DPA…</div>
      </Drawer>
    )
  }

  if (!proposalQ.data?.found) {
    return (
      <Drawer open onClose={onClose} title="Non trovato" width="md">
        <div className="p-6 text-zinc-500 text-sm">DPA non trovato.</div>
      </Drawer>
    )
  }

  const p = proposalQ.data.proposal
  const isDraft = p.status === 'draft'

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${p.code} R${p.revision}`}
      subtitle={p.title}
      badges={
        <>
          <Badge variant="success">DPA</Badge>
          <Badge variant="muted">{p.status}</Badge>
          {!isDraft && <Badge variant="warning">solo lettura</Badge>}
        </>
      }
      width="lg"
    >
      {!isDraft && (
        <div className="mx-4 mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Questo DPA è in stato <strong>{p.status}</strong> e non può essere modificato. Per
          cambiarne il contenuto crea una nuova revisione (R{p.revision + 1}) dal menu della lista.
          La preview e lo storico restano consultabili.
        </div>
      )}

      <div className="px-4 py-2 border-b border-zinc-800/80 bg-zinc-950/50 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={save.isPending || !isDraft}
          title={!isDraft ? 'Solo le bozze sono modificabili — crea una nuova revisione' : ''}
        >
          <Save className="size-3.5" />
          {save.isPending ? 'Salvataggio…' : 'Salva'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => window.open(`/api/proposals/${proposalId}/preview`, '_blank')}
        >
          <Eye className="size-3.5" /> Anteprima
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
          <GitBranch className="size-3.5" /> Storico
        </Button>
        {p.status === 'draft' && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const ok = await confirm({
                title: 'Marcare come inviato?',
                message:
                  'Il DPA risulterà inviato al Titolare, con la data di oggi. Procedere solo dopo aver effettivamente trasmesso il PDF al cliente.',
                confirmLabel: 'Marca come inviato',
                tone: 'info',
              })
              if (ok) send.mutate()
            }}
            disabled={send.isPending}
          >
            <Send className="size-3.5" /> Marca come inviato
          </Button>
        )}
        {save.isError && <span className="text-xs text-pink-300 ml-auto">Errore salvataggio</span>}
        {save.isSuccess && !save.isPending && (
          <span className="text-xs text-emerald-300 ml-auto">✓ Salvato</span>
        )}
      </div>

      <div className="border-b border-zinc-800/80 flex overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap transition border-b-2 -mb-px ${
              tab === t.id
                ? 'text-emerald-300 border-emerald-400'
                : 'text-zinc-500 border-transparent hover:text-zinc-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <fieldset
        disabled={!isDraft}
        className={`contents ${!isDraft ? '[&_input]:cursor-not-allowed [&_select]:cursor-not-allowed [&_textarea]:cursor-not-allowed' : ''}`}
      >
        <div className="p-5 space-y-4">
          {tab === 'meta' && (
            <MetaTab meta={meta} setMeta={setMeta} content={content} update={update} />
          )}
          {tab === 'parti' && <PartiTab content={content} update={update} />}
          {tab === 'oggetto' && (
            <OggettoTab content={content} setContent={setContent} update={update} />
          )}
          {tab === 'durata' && <DurataTab content={content} update={update} />}
          {tab === 'dati' && <DatiTab content={content} setContent={setContent} update={update} />}
          {tab === 'subresp' && (
            <SubRespTab content={content} setContent={setContent} update={update} />
          )}
          {tab === 'toms' && <TomsTab content={content} update={update} />}
          {tab === 'notifica' && <NotificaTab content={content} update={update} />}
          {tab === 'comunicazioni' && <ComunicazioniTab content={content} update={update} />}
          {tab === 'firme' && <FirmeTab content={content} update={update} />}
          {tab === 'asset' && <ProposalAssetsTab proposalId={proposalId} readOnly={!isDraft} />}
        </div>
      </fieldset>

      {historyOpen && (
        <ProposalHistoryDialog
          proposalId={proposalId}
          currentRevision={p.revision}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </Drawer>
  )
}

// ============================================================
// Metadata + Header + Premesse
// ============================================================
function MetaTab({
  meta,
  setMeta,
  content,
  update,
}: {
  meta: { title: string; subtitle: string; validityDays: string }
  setMeta: (m: typeof meta) => void
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const header = content.header ?? {}
  const premesse = content.premesse ?? {}
  return (
    <div className="space-y-5">
      <Section title="Top-level (riga in lista)">
        <Row>
          <Col span={8}>
            <Label>Titolo (top-level)</Label>
            <Input
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
            />
          </Col>
          <Col span={4}>
            <Label>Validità link pubblico (giorni)</Label>
            <Input
              type="number"
              value={meta.validityDays}
              onChange={(e) => setMeta({ ...meta, validityDays: e.target.value })}
            />
          </Col>
        </Row>
        <div>
          <Label>Sottotitolo</Label>
          <Input
            value={meta.subtitle}
            onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
          />
        </div>
      </Section>

      <Section title="Header del DPA">
        <Row>
          <Col span={6}>
            <Label>Titolo (header)</Label>
            <Input
              value={header.title ?? ''}
              onChange={(e) => update(['header', 'title'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <Label>Sottotitolo (header)</Label>
            <Input
              value={header.subtitle ?? ''}
              onChange={(e) => update(['header', 'subtitle'], e.target.value)}
              placeholder="Allegato al CTR-…"
            />
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Label>Data emissione (ISO)</Label>
            <Input
              type="date"
              value={header.issuedOn ?? ''}
              onChange={(e) => update(['header', 'issuedOn'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <Label>Luogo di stipula</Label>
            <Input
              value={header.luogoStipula ?? ''}
              onChange={(e) => update(['header', 'luogoStipula'], e.target.value)}
            />
          </Col>
        </Row>
      </Section>

      <Section title="Premesse">
        <ArrayEditor
          label="Riferimenti normativi"
          items={premesse.riferimentiNormativi ?? []}
          onAdd={() =>
            update(
              ['premesse', 'riferimentiNormativi'],
              [...(premesse.riferimentiNormativi ?? []), ''],
            )
          }
          onChange={(idx, v) => {
            const next = [...(premesse.riferimentiNormativi ?? [])]
            next[idx] = v
            update(['premesse', 'riferimentiNormativi'], next)
          }}
          onRemove={(idx) =>
            update(
              ['premesse', 'riferimentiNormativi'],
              (premesse.riferimentiNormativi ?? []).filter((_: unknown, i: number) => i !== idx),
            )
          }
        />
        <ArrayEditor
          label="Considerato che (numerati nel render)"
          items={premesse.consideratoChe ?? []}
          textarea
          onAdd={() =>
            update(['premesse', 'consideratoChe'], [...(premesse.consideratoChe ?? []), ''])
          }
          onChange={(idx, v) => {
            const next = [...(premesse.consideratoChe ?? [])]
            next[idx] = v
            update(['premesse', 'consideratoChe'], next)
          }}
          onRemove={(idx) =>
            update(
              ['premesse', 'consideratoChe'],
              (premesse.consideratoChe ?? []).filter((_: unknown, i: number) => i !== idx),
            )
          }
        />
        <div>
          <Label>Frase di chiusura premesse (sopra-esposto)</Label>
          <Textarea
            rows={2}
            value={premesse.sopraEsposto ?? ''}
            onChange={(e) => update(['premesse', 'sopraEsposto'], e.target.value)}
          />
        </div>
      </Section>
    </div>
  )
}

// ============================================================
// Parti — Art. 1 (Titolare + Responsabile + DPO + luoghi)
// ============================================================
function PartiTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const parties = content.parties ?? {}
  return (
    <div className="space-y-6">
      <PartySnapshotEditor
        label="TITOLARE DEL TRATTAMENTO (cliente)"
        party={parties.titolare ?? {}}
        path={['parties', 'titolare']}
        update={update}
      />
      <PartySnapshotEditor
        label="RESPONSABILE ESTERNO (G soLutions / tenant)"
        party={parties.responsabile ?? {}}
        path={['parties', 'responsabile']}
        update={update}
      />

      <Section title="DPO (Data Protection Officer)">
        <div className="text-xs text-zinc-500 mb-2">
          Opzionali: la maggior parte delle PMI non li ha designati. Compilare solo se presenti.
        </div>
        <DPOEditor
          label="DPO del Titolare"
          dpo={parties.dpoTitolare}
          path={['parties', 'dpoTitolare']}
          update={update}
        />
        <DPOEditor
          label="DPO del Responsabile"
          dpo={parties.dpoResponsabile}
          path={['parties', 'dpoResponsabile']}
          update={update}
        />
      </Section>

      <Section title="Luoghi di trattamento del Responsabile">
        <div className="text-xs text-zinc-500 mb-2">
          Sedi fisiche dove il Responsabile tratta i dati (esclusi sub-processor, che hanno una
          sezione dedicata). Es. "Sede del Responsabile: Formigine, IT", "Hetzner Online GmbH
          (Germania)".
        </div>
        <ArrayEditor
          label="Luoghi"
          items={parties.luoghiTrattamentoResponsabile ?? []}
          onAdd={() =>
            update(
              ['parties', 'luoghiTrattamentoResponsabile'],
              [...(parties.luoghiTrattamentoResponsabile ?? []), ''],
            )
          }
          onChange={(idx, v) => {
            const next = [...(parties.luoghiTrattamentoResponsabile ?? [])]
            next[idx] = v
            update(['parties', 'luoghiTrattamentoResponsabile'], next)
          }}
          onRemove={(idx) =>
            update(
              ['parties', 'luoghiTrattamentoResponsabile'],
              (parties.luoghiTrattamentoResponsabile ?? []).filter(
                (_: unknown, i: number) => i !== idx,
              ),
            )
          }
        />
      </Section>
    </div>
  )
}

function PartySnapshotEditor({
  label,
  party,
  path,
  update,
}: {
  label: string
  // biome-ignore lint/suspicious/noExplicitAny: party shape
  party: any
  path: string[]
  update: (p: string[], v: unknown) => void
}) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">{label}</div>
      <Row>
        <Col span={8}>
          <Label>Ragione sociale</Label>
          <Input
            value={party.ragioneSociale ?? ''}
            onChange={(e) => update([...path, 'ragioneSociale'], e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Label>P.IVA</Label>
          <Input
            value={party.partitaIva ?? ''}
            onChange={(e) => update([...path, 'partitaIva'], e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          <Label>Indirizzo</Label>
          <Input
            value={party.sedeLegale?.indirizzo ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'indirizzo'], e.target.value)}
          />
        </Col>
        <Col span={2}>
          <Label>CAP</Label>
          <Input
            value={party.sedeLegale?.cap ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'cap'], e.target.value)}
          />
        </Col>
        <Col span={3}>
          <Label>Città</Label>
          <Input
            value={party.sedeLegale?.citta ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'citta'], e.target.value)}
          />
        </Col>
        <Col span={1}>
          <Label>Prov</Label>
          <Input
            value={party.sedeLegale?.provincia ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'provincia'], e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <Label>Codice fiscale</Label>
          <Input
            value={party.codiceFiscale ?? ''}
            onChange={(e) => update([...path, 'codiceFiscale'], e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Label>REA</Label>
          <Input
            value={party.rea ?? ''}
            onChange={(e) => update([...path, 'rea'], e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Label>Qualifica</Label>
          <Input
            value={party.qualifica ?? ''}
            onChange={(e) => update([...path, 'qualifica'], e.target.value)}
            placeholder="Presidente / Titolare / …"
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <Label>Legale rappresentante</Label>
          <Input
            value={party.legaleRappresentante ?? ''}
            onChange={(e) => update([...path, 'legaleRappresentante'], e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Label>Email</Label>
          <Input
            value={party.email ?? ''}
            onChange={(e) => update([...path, 'email'], e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Label>PEC</Label>
          <Input
            value={party.pec ?? ''}
            onChange={(e) => update([...path, 'pec'], e.target.value)}
          />
        </Col>
      </Row>
    </div>
  )
}

function DPOEditor({
  label,
  dpo,
  path,
  update,
}: {
  label: string
  // biome-ignore lint/suspicious/noExplicitAny: dpo shape
  dpo: any
  path: string[]
  update: (p: string[], v: unknown) => void
}) {
  const present = !!dpo && (dpo.nome || dpo.email)
  return (
    <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-3 mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-zinc-300">{label}</span>
        {present ? (
          <Button variant="ghost" size="sm" onClick={() => update(path, undefined)}>
            <Trash2 className="size-3" /> Rimuovi DPO
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update(path, { nome: '', email: '', tipo: 'esterno' })}
          >
            <Plus className="size-3" /> Aggiungi DPO
          </Button>
        )}
      </div>
      {present && (
        <Row>
          <Col span={4}>
            <Label>Nome</Label>
            <Input
              value={dpo.nome ?? ''}
              onChange={(e) => update([...path, 'nome'], e.target.value)}
            />
          </Col>
          <Col span={4}>
            <Label>Email</Label>
            <Input
              value={dpo.email ?? ''}
              onChange={(e) => update([...path, 'email'], e.target.value)}
            />
          </Col>
          <Col span={2}>
            <Label>Telefono</Label>
            <Input
              value={dpo.telefono ?? ''}
              onChange={(e) => update([...path, 'telefono'], e.target.value)}
            />
          </Col>
          <Col span={2}>
            <Label>Tipo</Label>
            <Select
              value={dpo.tipo ?? 'esterno'}
              onChange={(e) => update([...path, 'tipo'], e.target.value)}
            >
              <option value="interno">interno</option>
              <option value="esterno">esterno</option>
            </Select>
          </Col>
        </Row>
      )}
    </div>
  )
}

// ============================================================
// Oggetto & Finalità — Art. 2
// ============================================================
function OggettoTab({
  content,
  setContent: _setContent,
  update,
}: {
  content: DPAContent
  setContent: (c: DPAContent) => void
  update: (path: string[], v: unknown) => void
}) {
  const o = content.oggettoFinalita ?? {}
  return (
    <div className="space-y-4">
      <div>
        <Label>Descrizione servizio (markdown-lite, supporta **bold** e *italic*)</Label>
        <Textarea
          rows={4}
          value={o.descrizioneServizio ?? ''}
          onChange={(e) => update(['oggettoFinalita', 'descrizioneServizio'], e.target.value)}
        />
      </div>
      <ArrayEditor
        label="Finalità del trattamento (art. 5.1.b limitazione)"
        items={o.finalita ?? []}
        onAdd={() => update(['oggettoFinalita', 'finalita'], [...(o.finalita ?? []), ''])}
        onChange={(idx, v) => {
          const next = [...(o.finalita ?? [])]
          next[idx] = v
          update(['oggettoFinalita', 'finalita'], next)
        }}
        onRemove={(idx) =>
          update(
            ['oggettoFinalita', 'finalita'],
            (o.finalita ?? []).filter((_: unknown, i: number) => i !== idx),
          )
        }
      />
      <div>
        <Label>Contratto di riferimento (codice CTR)</Label>
        <Input
          value={o.contrattoDiRiferimento ?? ''}
          onChange={(e) => update(['oggettoFinalita', 'contrattoDiRiferimento'], e.target.value)}
          placeholder="CTR-2026-015"
        />
        <div className="text-xs text-zinc-500 mt-1">
          Se il DPA è Allegato B di un contratto specifico, il renderer mostra il riferimento
          incrociato.
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Durata · Cessazione — Art. 3 + 11
// ============================================================
function DurataTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const d = content.durataTrattamento ?? {}
  const ces = content.cessazione ?? {}
  return (
    <div className="space-y-5">
      <Section title="Art. 3 — Durata del trattamento">
        <div>
          <Label>Modalità</Label>
          <Select
            value={d.coincideConDurataContratto ? 'coincide' : 'esplicita'}
            onChange={(e) =>
              update(
                ['durataTrattamento', 'coincideConDurataContratto'],
                e.target.value === 'coincide',
              )
            }
          >
            <option value="coincide">Coincide con la durata del contratto di riferimento</option>
            <option value="esplicita">Durata esplicita (mesi)</option>
          </Select>
        </div>
        {!d.coincideConDurataContratto && (
          <div>
            <Label>Durata esplicita in mesi</Label>
            <Input
              type="number"
              value={d.durataMesiEsplicita ?? ''}
              onChange={(e) =>
                update(
                  ['durataTrattamento', 'durataMesiEsplicita'],
                  Number(e.target.value) || undefined,
                )
              }
            />
          </div>
        )}
        <div>
          <Label>A cessazione (cosa si fa con i dati)</Label>
          <Select
            value={d.aCessazione ?? 'delete'}
            onChange={(e) => update(['durataTrattamento', 'aCessazione'], e.target.value)}
          >
            <option value="delete">Cancellazione</option>
            <option value="return">Restituzione</option>
            <option value="both">Restituzione + cancellazione</option>
            <option value="as-instructed">Su istruzioni scritte del Titolare</option>
          </Select>
        </div>
        <div>
          <Label>Note specifiche sulla cessazione</Label>
          <Textarea
            rows={2}
            value={d.note ?? ''}
            onChange={(e) => update(['durataTrattamento', 'note'], e.target.value)}
          />
        </div>
      </Section>

      <Section title="Art. 11 — Cessazione (tempi e legali)">
        <Row>
          <Col span={6}>
            <Label>Giorni max per restituzione/cancellazione</Label>
            <Input
              type="number"
              value={ces.giorniMaxRestituzioneOCancellazione ?? 30}
              onChange={(e) =>
                update(
                  ['cessazione', 'giorniMaxRestituzioneOCancellazione'],
                  Number(e.target.value),
                )
              }
            />
          </Col>
          <Col span={6}>
            <Label>Eccezione obblighi di legge</Label>
            <Select
              value={ces.eccezioneObbligoLegale ? 'true' : 'false'}
              onChange={(e) =>
                update(['cessazione', 'eccezioneObbligoLegale'], e.target.value === 'true')
              }
            >
              <option value="true">Sì (consigliato)</option>
              <option value="false">No</option>
            </Select>
          </Col>
        </Row>
      </Section>
    </div>
  )
}

// ============================================================
// Natura dei dati — Art. 4
// ============================================================
function DatiTab({
  content,
  setContent: _setContent,
  update,
}: {
  content: DPAContent
  setContent: (c: DPAContent) => void
  update: (path: string[], v: unknown) => void
}) {
  const n = content.naturaDati ?? {
    categorieDati: [],
    categorieSpeciali: [],
    categorieInteressati: [],
    minoriPresenti: false,
  }
  const toggleSpecial = (key: string) => {
    const current = new Set<string>(n.categorieSpeciali ?? [])
    if (current.has(key)) current.delete(key)
    else current.add(key)
    update(['naturaDati', 'categorieSpeciali'], Array.from(current))
  }
  return (
    <div className="space-y-5">
      <ArrayEditor
        label="Categorie di dati personali trattati"
        items={n.categorieDati ?? []}
        onAdd={() => update(['naturaDati', 'categorieDati'], [...(n.categorieDati ?? []), ''])}
        onChange={(idx, v) => {
          const next = [...(n.categorieDati ?? [])]
          next[idx] = v
          update(['naturaDati', 'categorieDati'], next)
        }}
        onRemove={(idx) =>
          update(
            ['naturaDati', 'categorieDati'],
            (n.categorieDati ?? []).filter((_: unknown, i: number) => i !== idx),
          )
        }
      />

      <Section title="Categorie particolari ex art. 9 GDPR">
        <div className="text-xs text-zinc-500 mb-2">
          Spunta SOLO se queste categorie vengono effettivamente trattate dal Responsabile nel
          contesto del servizio. La presenza attiva un warning rafforzato nel renderer.
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SPECIAL_CATEGORIES.map((s) => {
            const checked = (n.categorieSpeciali ?? []).includes(s.key)
            return (
              <label
                key={s.key}
                className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-xs cursor-pointer hover:bg-zinc-900/40"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSpecial(s.key)}
                  className="accent-emerald-500"
                />
                <span className={checked ? 'text-amber-200' : 'text-zinc-300'}>{s.label}</span>
              </label>
            )
          })}
        </div>
      </Section>

      <ArrayEditor
        label="Categorie di interessati"
        items={n.categorieInteressati ?? []}
        onAdd={() =>
          update(['naturaDati', 'categorieInteressati'], [...(n.categorieInteressati ?? []), ''])
        }
        onChange={(idx, v) => {
          const next = [...(n.categorieInteressati ?? [])]
          next[idx] = v
          update(['naturaDati', 'categorieInteressati'], next)
        }}
        onRemove={(idx) =>
          update(
            ['naturaDati', 'categorieInteressati'],
            (n.categorieInteressati ?? []).filter((_: unknown, i: number) => i !== idx),
          )
        }
      />

      <div>
        <label className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!n.minoriPresenti}
            onChange={(e) => update(['naturaDati', 'minoriPresenti'], e.target.checked)}
            className="accent-emerald-500"
          />
          <span className="font-semibold">⚠ Presenza di dati riferiti a minori</span>
          <span className="text-zinc-500 ml-2">
            Attiva il warning GDPR-rafforzato (art. 8 GDPR + 2-quinquies D.lgs. 196/2003).
          </span>
        </label>
      </div>
    </div>
  )
}

// ============================================================
// Sub-responsabili — Art. 6
// ============================================================
function SubRespTab({
  content,
  setContent: _setContent,
  update,
}: {
  content: DPAContent
  setContent: (c: DPAContent) => void
  update: (path: string[], v: unknown) => void
}) {
  const s = content.subResponsabili ?? {
    autorizzazioneGenerale: true,
    elenco: [],
    preavvisoModificheGiorni: 30,
    canaleNotificaCambi: [],
    dirittoObiezioneTitolare: true,
    giorniPerObiezione: 15,
  }
  const list: Array<Record<string, unknown>> = s.elenco ?? []

  const updateElenco = (next: Array<Record<string, unknown>>) =>
    update(['subResponsabili', 'elenco'], next)

  return (
    <div className="space-y-5">
      <Section title="Modalità di autorizzazione">
        <div>
          <Label>Autorizzazione</Label>
          <Select
            value={s.autorizzazioneGenerale ? 'generale' : 'specifica'}
            onChange={(e) =>
              update(['subResponsabili', 'autorizzazioneGenerale'], e.target.value === 'generale')
            }
          >
            <option value="generale">Generale (art. 28.2 secondo paragrafo)</option>
            <option value="specifica">Specifica per ogni sub-responsabile (art. 28.2 primo)</option>
          </Select>
        </div>
      </Section>

      <Section title="Elenco sub-responsabili">
        {list.length === 0 && (
          <div className="text-xs text-zinc-500 italic">
            Nessun sub-responsabile elencato. Aggiungere se il Responsabile si avvale di servizi
            terzi (hosting, storage, CDN, email, ecc.).
          </div>
        )}
        <div className="space-y-3">
          {list.map((sp, idx) => (
            <div
              key={`${sp.name ?? 'sub'}-${idx}`}
              className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 space-y-2"
            >
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-zinc-300">#{idx + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => updateElenco(list.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="size-3" /> Rimuovi
                </Button>
              </div>
              <Row>
                <Col span={6}>
                  <Label>Nome</Label>
                  <Input
                    value={(sp.name as string) ?? ''}
                    onChange={(e) =>
                      updateElenco(
                        list.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                </Col>
                <Col span={6}>
                  <Label>Paese</Label>
                  <Input
                    value={(sp.paese as string) ?? ''}
                    onChange={(e) =>
                      updateElenco(
                        list.map((x, i) => (i === idx ? { ...x, paese: e.target.value } : x)),
                      )
                    }
                  />
                </Col>
              </Row>
              <div>
                <Label>Servizio</Label>
                <Input
                  value={(sp.servizio as string) ?? ''}
                  onChange={(e) =>
                    updateElenco(
                      list.map((x, i) => (i === idx ? { ...x, servizio: e.target.value } : x)),
                    )
                  }
                />
              </div>
              <div>
                <Label>Privacy / DPA URL</Label>
                <Input
                  value={(sp.dataProtectionUrl as string) ?? ''}
                  onChange={(e) =>
                    updateElenco(
                      list.map((x, i) =>
                        i === idx ? { ...x, dataProtectionUrl: e.target.value } : x,
                      ),
                    )
                  }
                />
              </div>
              <Row>
                <Col span={6}>
                  <label className="flex items-center gap-2 text-xs mt-1">
                    <input
                      type="checkbox"
                      checked={!!sp.transferOutsideEEA}
                      onChange={(e) =>
                        updateElenco(
                          list.map((x, i) =>
                            i === idx ? { ...x, transferOutsideEEA: e.target.checked } : x,
                          ),
                        )
                      }
                      className="accent-emerald-500"
                    />
                    Trasferisce dati fuori dallo SEE
                  </label>
                </Col>
                <Col span={6}>
                  <Label>Meccanismo SCC (se applicabile)</Label>
                  <Input
                    value={(sp.sccMechanism as string) ?? ''}
                    onChange={(e) =>
                      updateElenco(
                        list.map((x, i) =>
                          i === idx ? { ...x, sccMechanism: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="SCC UE 2021/914"
                  />
                </Col>
              </Row>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            updateElenco([
              ...list,
              { name: '', paese: '', servizio: '', transferOutsideEEA: false },
            ])
          }
        >
          <Plus className="size-3.5" /> Aggiungi sub-responsabile
        </Button>
      </Section>

      <Section title="Notifica modifiche e diritto obiezione">
        <Row>
          <Col span={6}>
            <Label>Preavviso modifiche (giorni)</Label>
            <Input
              type="number"
              value={s.preavvisoModificheGiorni ?? 30}
              onChange={(e) =>
                update(['subResponsabili', 'preavvisoModificheGiorni'], Number(e.target.value))
              }
            />
          </Col>
          <Col span={6}>
            <Label>Giorni per obiezione</Label>
            <Input
              type="number"
              value={s.giorniPerObiezione ?? 15}
              onChange={(e) =>
                update(['subResponsabili', 'giorniPerObiezione'], Number(e.target.value))
              }
            />
          </Col>
        </Row>
        <div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!s.dirittoObiezioneTitolare}
              onChange={(e) =>
                update(['subResponsabili', 'dirittoObiezioneTitolare'], e.target.checked)
              }
              className="accent-emerald-500"
            />
            Il Titolare ha diritto di obiezione vincolante
          </label>
        </div>
        <ArrayEditor
          label="Canali notifica cambi (se vuoto, fallback a comunicazioni.titolareEmail)"
          items={s.canaleNotificaCambi ?? []}
          onAdd={() =>
            update(
              ['subResponsabili', 'canaleNotificaCambi'],
              [...(s.canaleNotificaCambi ?? []), ''],
            )
          }
          onChange={(idx, v) => {
            const next = [...(s.canaleNotificaCambi ?? [])]
            next[idx] = v
            update(['subResponsabili', 'canaleNotificaCambi'], next)
          }}
          onRemove={(idx) =>
            update(
              ['subResponsabili', 'canaleNotificaCambi'],
              (s.canaleNotificaCambi ?? []).filter((_: unknown, i: number) => i !== idx),
            )
          }
        />
      </Section>
    </div>
  )
}

// ============================================================
// TOMs — Art. 7 + trasferimenti extra-EEA Art. 12
// ============================================================
function TomsTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const t = content.toms ?? {}
  const tr = content.trasferimentiExtraEEA ?? { presenti: false }
  return (
    <div className="space-y-5">
      <Section title="Art. 7 — Misure tecniche e organizzative (art. 32)">
        <div>
          <Label>Cifratura in transito</Label>
          <Textarea
            rows={2}
            value={t.cifraturaInTransit ?? ''}
            onChange={(e) => update(['toms', 'cifraturaInTransit'], e.target.value)}
          />
        </div>
        <div>
          <Label>Cifratura at-rest</Label>
          <Textarea
            rows={2}
            value={t.cifraturaAtRest ?? ''}
            onChange={(e) => update(['toms', 'cifraturaAtRest'], e.target.value)}
          />
        </div>
        <div>
          <Label>Backup</Label>
          <Textarea
            rows={2}
            value={t.backup ?? ''}
            onChange={(e) => update(['toms', 'backup'], e.target.value)}
          />
        </div>
        <div>
          <Label>Controllo accessi</Label>
          <Textarea
            rows={2}
            value={t.controlloAccessi ?? ''}
            onChange={(e) => update(['toms', 'controlloAccessi'], e.target.value)}
          />
        </div>
        <div>
          <Label>Logging</Label>
          <Textarea
            rows={2}
            value={t.logging ?? ''}
            onChange={(e) => update(['toms', 'logging'], e.target.value)}
          />
        </div>
        <div>
          <Label>Disaster recovery</Label>
          <Textarea
            rows={2}
            value={t.disasterRecovery ?? ''}
            onChange={(e) => update(['toms', 'disasterRecovery'], e.target.value)}
          />
        </div>
        <div>
          <Label>Pseudonimizzazione</Label>
          <Textarea
            rows={2}
            value={t.pseudonimizzazione ?? ''}
            onChange={(e) => update(['toms', 'pseudonimizzazione'], e.target.value)}
          />
        </div>
        <ArrayEditor
          label="Altre misure"
          items={t.altre ?? []}
          textarea
          onAdd={() => update(['toms', 'altre'], [...(t.altre ?? []), ''])}
          onChange={(idx, v) => {
            const next = [...(t.altre ?? [])]
            next[idx] = v
            update(['toms', 'altre'], next)
          }}
          onRemove={(idx) =>
            update(
              ['toms', 'altre'],
              (t.altre ?? []).filter((_: unknown, i: number) => i !== idx),
            )
          }
        />
      </Section>

      <Section title="Art. 12 — Trasferimenti extra-SEE">
        <div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!tr.presenti}
              onChange={(e) => update(['trasferimentiExtraEEA', 'presenti'], e.target.checked)}
              className="accent-emerald-500"
            />
            Presenti trasferimenti fuori dallo Spazio Economico Europeo
          </label>
        </div>
        {tr.presenti && (
          <div>
            <Label>Meccanismo di trasferimento</Label>
            <Input
              value={tr.meccanismo ?? ''}
              onChange={(e) => update(['trasferimentiExtraEEA', 'meccanismo'], e.target.value)}
              placeholder="SCC UE 2021/914 / Adeguatezza UE / BCR…"
            />
          </div>
        )}
      </Section>
    </div>
  )
}

// ============================================================
// Notifica violazioni + Audit + Assistenza — Art. 8, 9, 10
// ============================================================
function NotificaTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const nv = content.notificaViolazioni ?? {}
  const a = content.audit ?? {}
  const at = content.assistenzaTitolare ?? {}
  return (
    <div className="space-y-5">
      <Section title="Art. 8 — Assistenza al Titolare">
        <div className="space-y-2">
          {[
            { k: 'dirittiInteressati', label: 'Diritti interessati (artt. 15-22)' },
            { k: 'dpia', label: 'DPIA e consultazione preventiva (artt. 35-36)' },
            { k: 'sicurezzaEDataBreach', label: 'Sicurezza e data breach (artt. 32-34)' },
          ].map((row) => (
            <label key={row.k} className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!at[row.k]}
                onChange={(e) => update(['assistenzaTitolare', row.k], e.target.checked)}
                className="accent-emerald-500"
              />
              {row.label}
            </label>
          ))}
        </div>
        <div>
          <Label>Note</Label>
          <Textarea
            rows={2}
            value={at.note ?? ''}
            onChange={(e) => update(['assistenzaTitolare', 'note'], e.target.value)}
          />
        </div>
      </Section>

      <Section title="Art. 9 — Notifica violazioni (data breach)">
        <Row>
          <Col span={6}>
            <Label>Ore massime per notificare al Titolare</Label>
            <Input
              type="number"
              value={nv.oreMaxNotifica ?? 48}
              onChange={(e) =>
                update(['notificaViolazioni', 'oreMaxNotifica'], Number(e.target.value))
              }
            />
          </Col>
          <Col span={6}>
            <label className="flex items-center gap-2 text-xs mt-7">
              <input
                type="checkbox"
                checked={!!nv.registroBreach}
                onChange={(e) => update(['notificaViolazioni', 'registroBreach'], e.target.checked)}
                className="accent-emerald-500"
              />
              Il Responsabile mantiene un registro dei breach
            </label>
          </Col>
        </Row>
        <ArrayEditor
          label="Canali di notifica (email / PEC del Titolare)"
          items={nv.canaliNotifica ?? []}
          onAdd={() =>
            update(['notificaViolazioni', 'canaliNotifica'], [...(nv.canaliNotifica ?? []), ''])
          }
          onChange={(idx, v) => {
            const next = [...(nv.canaliNotifica ?? [])]
            next[idx] = v
            update(['notificaViolazioni', 'canaliNotifica'], next)
          }}
          onRemove={(idx) =>
            update(
              ['notificaViolazioni', 'canaliNotifica'],
              (nv.canaliNotifica ?? []).filter((_: unknown, i: number) => i !== idx),
            )
          }
        />
      </Section>

      <Section title="Art. 10 — Audit">
        <div>
          <Label>Descrizione</Label>
          <Textarea
            rows={3}
            value={a.descrizione ?? ''}
            onChange={(e) => update(['audit', 'descrizione'], e.target.value)}
          />
        </div>
        <Row>
          <Col span={6}>
            <Label>Preavviso (giorni lavorativi)</Label>
            <Input
              type="number"
              value={a.preavvisoGiorni ?? 30}
              onChange={(e) => update(['audit', 'preavvisoGiorni'], Number(e.target.value))}
            />
          </Col>
          <Col span={6}>
            <label className="flex items-center gap-2 text-xs mt-7">
              <input
                type="checkbox"
                checked={!!a.costiACaricoTitolare}
                onChange={(e) => update(['audit', 'costiACaricoTitolare'], e.target.checked)}
                className="accent-emerald-500"
              />
              Costi a carico del Titolare (consigliato)
            </label>
          </Col>
        </Row>
      </Section>
    </div>
  )
}

// ============================================================
// Comunicazioni + Disposizioni finali — Art. 13 + 14
// ============================================================
function ComunicazioniTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const c = content.comunicazioni ?? {}
  const df = content.disposizioniFinali ?? {}
  return (
    <div className="space-y-5">
      <Section title="Art. 13 — Comunicazioni">
        <Row>
          <Col span={6}>
            <Label>Email Titolare</Label>
            <Input
              value={c.titolareEmail ?? ''}
              onChange={(e) => update(['comunicazioni', 'titolareEmail'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <Label>Email Responsabile</Label>
            <Input
              value={c.responsabileEmail ?? ''}
              onChange={(e) => update(['comunicazioni', 'responsabileEmail'], e.target.value)}
            />
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!c.pecValida}
                onChange={(e) => update(['comunicazioni', 'pecValida'], e.target.checked)}
                className="accent-emerald-500"
              />
              Entrambe le parti hanno PEC valida
            </label>
          </Col>
          <Col span={6}>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!c.emailValida}
                onChange={(e) => update(['comunicazioni', 'emailValida'], e.target.checked)}
                className="accent-emerald-500"
              />
              Entrambe le parti hanno email valida
            </label>
          </Col>
        </Row>
        <div>
          <Label>Canale richieste interessati (artt. 15-22)</Label>
          <Input
            value={c.canaleRichiesteInteressati ?? ''}
            onChange={(e) =>
              update(['comunicazioni', 'canaleRichiesteInteressati'], e.target.value)
            }
            placeholder="email a cui il Titolare inoltra le richieste degli interessati"
          />
        </div>
      </Section>

      <Section title="Art. 14 — Disposizioni finali">
        <div>
          <Label>Legge applicabile</Label>
          <Input
            value={df.leggeApplicabile ?? ''}
            onChange={(e) => update(['disposizioniFinali', 'leggeApplicabile'], e.target.value)}
          />
        </div>
        <Row>
          <Col span={6}>
            <Label>Foro competente</Label>
            <Input
              value={df.foro ?? ''}
              onChange={(e) => update(['disposizioniFinali', 'foro'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <label className="flex items-center gap-2 text-xs mt-7">
              <input
                type="checkbox"
                checked={!!df.mediazioneObbligatoria}
                onChange={(e) =>
                  update(['disposizioniFinali', 'mediazioneObbligatoria'], e.target.checked)
                }
                className="accent-emerald-500"
              />
              Mediazione obbligatoria prima del contenzioso
            </label>
          </Col>
        </Row>
      </Section>
    </div>
  )
}

// ============================================================
// Firme
// ============================================================
function FirmeTab({
  content,
  update,
}: {
  content: DPAContent
  update: (path: string[], v: unknown) => void
}) {
  const f = content.firme ?? {}
  const t = f.titolare ?? {}
  const r = f.responsabile ?? {}
  return (
    <div className="space-y-5">
      <div>
        <Label>Luogo e data</Label>
        <Input
          value={f.luogoData ?? ''}
          onChange={(e) => update(['firme', 'luogoData'], e.target.value)}
          placeholder="Formigine, 13/05/2026"
        />
      </div>

      <Section title="Titolare">
        <Row>
          <Col span={6}>
            <Label>Ragione sociale</Label>
            <Input
              value={t.ragioneSociale ?? ''}
              onChange={(e) => update(['firme', 'titolare', 'ragioneSociale'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <Label>Qualifica</Label>
            <Input
              value={t.qualifica ?? ''}
              onChange={(e) => update(['firme', 'titolare', 'qualifica'], e.target.value)}
            />
          </Col>
        </Row>
        <div>
          <Label>Legale rappresentante</Label>
          <Input
            value={t.legaleRappresentante ?? ''}
            onChange={(e) => update(['firme', 'titolare', 'legaleRappresentante'], e.target.value)}
          />
        </div>
      </Section>

      <Section title="Responsabile">
        <Row>
          <Col span={6}>
            <Label>Ragione sociale</Label>
            <Input
              value={r.ragioneSociale ?? ''}
              onChange={(e) => update(['firme', 'responsabile', 'ragioneSociale'], e.target.value)}
            />
          </Col>
          <Col span={6}>
            <Label>Qualifica</Label>
            <Input
              value={r.qualifica ?? ''}
              onChange={(e) => update(['firme', 'responsabile', 'qualifica'], e.target.value)}
            />
          </Col>
        </Row>
        <div>
          <Label>Legale rappresentante</Label>
          <Input
            value={r.legaleRappresentante ?? ''}
            onChange={(e) =>
              update(['firme', 'responsabile', 'legaleRappresentante'], e.target.value)
            }
          />
        </div>
      </Section>
    </div>
  )
}

// ============================================================
// Shared helpers
// ============================================================
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">{title}</div>
      {children}
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-12 gap-3">{children}</div>
}

function Col({ span, children }: { span: number; children: React.ReactNode }) {
  return <div className={`col-span-${span}`}>{children}</div>
}

function ArrayEditor({
  label,
  items,
  textarea,
  onAdd,
  onChange,
  onRemove,
}: {
  label: string
  items: string[]
  textarea?: boolean
  onAdd: () => void
  onChange: (idx: number, value: string) => void
  onRemove: (idx: number) => void
}) {
  const Input2 = textarea ? Textarea : Input
  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2 mb-2">
        {items.map((item, idx) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: free-form string list, no stable id
          <div key={`${label}-${idx}-${item.slice(0, 20)}`} className="flex items-start gap-2">
            <Input2
              className="flex-1"
              rows={textarea ? 2 : undefined}
              value={item}
              onChange={(e: { target: { value: string } }) => onChange(idx, e.target.value)}
            />
            <Button variant="ghost" size="sm" onClick={() => onRemove(idx)}>
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="size-3.5" /> Aggiungi
      </Button>
    </div>
  )
}

function setDeep(obj: DPAContent, path: string[], value: unknown): DPAContent {
  if (path.length === 0) return value as DPAContent
  const head = path[0]
  const rest = path.slice(1)
  if (head === undefined) return value as DPAContent
  const current = (obj?.[head] as DPAContent) ?? {}
  return {
    ...(obj ?? {}),
    [head]: rest.length === 0 ? value : setDeep(current, rest, value),
  }
}
